import "./Flow.scss";

import { Box, Card, Grid, IconButton, Typography } from "@material-ui/core";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import Close from "@material-ui/icons/Close";
import { Form, Formik } from "formik";
import React, { useMemo } from "react";

import i18n from "../services/i18n";
import { CommonErrorDialog } from "./CommonErrorDialog";
import { FormProgress } from "./index";
import { Button, SubmitButton } from "./input/elements";

export interface StepDescriptor {
  autofilledData?: (...args: any) => any;
  dotNumber: number;
  backButtonText: (...args: any) => string;
  backButtonVisible: boolean;
  backStep: (...args: any) => number;
  backStepOnBackButton: boolean;
  className?: string;
  nextButtonText: (...args: any) => string;
  nextButtonVisible: boolean;
  nextStep: (...args: any) => number;
  nextStepOnNextButton: boolean;
  onBackButton?: (...args: any) => void;
  onNextButton?: (...args: any) => void;
  onValidate: (...args: any) => any;
  subform: (props: any) => JSX.Element;
  subtitle: (...args: any) => string;
  title: (...args: any) => string;
  titleClassName?: string;
}

export interface FlowProps {
  error: string;
  initialValues: any;
  maxNumberOfSteps: number;
  numberOfSteps: number;
  setError: (error: string) => void;
  setStep: (step: number) => void;
  step: number;
  stepsDescriptors: Map<number, Partial<StepDescriptor>>;
  submitInProgress: boolean;
}

export const Flow = (props: FlowProps) => {
  // Props
  const {
    error,
    initialValues,
    maxNumberOfSteps,
    numberOfSteps,
    setError,
    setStep,
    step,
    stepsDescriptors,
    submitInProgress,
  } = props;

  // Helpers
  const isFirstStep = (step: number): boolean => {
    return step === 0;
  };

  const isLastStep = (step: number): boolean => {
    return step === maxNumberOfSteps - 1;
  };

  const defaultStepDescriptor = (step: number) : StepDescriptor => ({
    dotNumber: step,
    backButtonText: () => i18n.t("Back"),
    backButtonVisible: true,
    backStep: () => !isFirstStep(step) ? step - 1 : step,
    backStepOnBackButton: true,
    nextButtonText: () => i18n.t("Next"),
    nextButtonVisible: true,
    nextStep: () => !isLastStep(step) ? step + 1 : step,
    nextStepOnNextButton: true,
    onValidate: () => ({}),
    subform: () => <></>,
    subtitle: () => "",
    title: () => "",
    titleClassName: "title--big",
  });

  // Steps
  const completeStepsDescriptor : Map<number, StepDescriptor> = useMemo(
    () => {
      const stepsDescriptorClone = new Map<number, StepDescriptor>();

      stepsDescriptors.forEach((
        value: Partial<StepDescriptor>, key: number
      ) => {
        stepsDescriptorClone.set(
          key,
          {
            ...defaultStepDescriptor(step),
            ...value,
          }
        );
      });

      return stepsDescriptorClone;
    },
    [stepsDescriptors]
  )

  // Validation
  const onValidate = (values: any) => {
    return completeStepsDescriptor.get(step)?.onValidate(values);
  };

  // Transitions
  const onPreviousStep = (
    values: any, {
      setSubmitting, setTouched
    }: any
  ) => {
    setSubmitting(true);

    const stepDescriptor = completeStepsDescriptor.get(step) || defaultStepDescriptor(step);

    const {
      backStep,
      backStepOnBackButton,
      onBackButton,
    } = stepDescriptor;

    if (onBackButton) {
      onBackButton(values);
    }

    if (backStepOnBackButton) {
      setStep(backStep({ values }));
    }

    setTouched({});

    setSubmitting(false);
  };

  const onNextStep = async (
    values: any,
    {
      setSubmitting, setTouched
    }: any
  ) => {
    setSubmitting(true);

    const stepDescriptor = completeStepsDescriptor.get(step) || defaultStepDescriptor(step);

    const {
      nextStep,
      nextStepOnNextButton,
      onNextButton,
    } = stepDescriptor;

    if (onNextButton) {
      onNextButton(values);
    }

    if (nextStepOnNextButton) {
      setStep(nextStep({ values }));
    }

    setTouched({});

    setSubmitting(false);
  };

  // Options
  const stepDescriptor = completeStepsDescriptor.get(step) || defaultStepDescriptor(step);

  const {
    autofilledData,
    backButtonText,
    backButtonVisible,
    className,
    dotNumber,
    nextButtonText,
    nextButtonVisible,
    subform,
    titleClassName,
  } = stepDescriptor;

  return (
    <Formik
      initialValues={initialValues}
      validate={onValidate}
      onSubmit={onNextStep}
    >
      {({
        values,
        errors,
        isSubmitting,
        touched,
        setFieldValue,
        setSubmitting,
        setTouched,
        setValues,
        submitForm,
      }) => {
        const formProps = {
          errors,
          isSubmitting,
          setFieldValue,
          setSubmitting,
          setTouched,
          setValues,
          submitForm,
          touched,
          values,
        };

        const buttonProps = { ...formProps } as any;
        delete buttonProps.setFieldValue;
        delete buttonProps.setValues;

        const titleText = stepDescriptor.title({ values });
        const subtitleText = stepDescriptor.subtitle({ values });

        return (
          <Box className={className}>
            <Box display="grid">
              <IconButton
                className="secondary--small autofill"
                onClick={() => {
                  if (autofilledData) {
                    setValues(autofilledData(values))
                  }
                }}
              >
                <AutorenewIcon />
              </IconButton>
            </Box>

            <Card className="card">
              <IconButton
                className="close-icon"
                onClick={() => {
                  history.go(-2);
                }}
              >
                <Close />
              </IconButton>

              {titleText &&
              <Typography className={titleClassName} variant="subtitle1">
                {titleText}
              </Typography>
              }

              {subtitleText &&
              <Typography className="subtitle--big" variant="subtitle2">
                {subtitleText}
              </Typography>
              }

              <Form autoComplete="off">
                <Grid justify="center" container spacing={5}>
                  <Grid item xs={12}>
                    {subform(formProps)}
                  </Grid>

                  {(backButtonVisible || nextButtonVisible) &&
                  <Grid
                    container
                    item
                    direction={backButtonVisible ? "row" : "column"}
                    spacing={backButtonVisible ? 6 : 0}
                  >
                    {backButtonVisible &&
                    <Grid
                      item
                      lg={6}
                      xs={6}
                      md={6}
                      className="button-container"
                    >
                      <Button
                        disabled={submitInProgress || isSubmitting}
                        onClick={() =>
                          onPreviousStep(
                            values,
                            {
                              setSubmitting,
                              setTouched
                            }
                          )
                        }
                        value={backButtonText()}
                      />
                    </Grid>
                    }

                    {nextButtonVisible &&
                    <Grid
                      item
                      lg={backButtonVisible ? 6 : 12}
                      xs={backButtonVisible ? 6 : false}
                      md={backButtonVisible ? 6 : false}
                      className="button-container"
                    >
                      <SubmitButton
                        value={nextButtonText()}
                        className={"primary"}
                        {...buttonProps}
                        isSubmitting={submitInProgress || isSubmitting}
                      />
                    </Grid>
                    }
                  </Grid>
                  }
                </Grid>
              </Form>

              <CommonErrorDialog error={error} setError={setError} />
            </Card>

            <FormProgress
              maxStep={numberOfSteps}
              step={dotNumber}
              onClick={(step: number) => setStep(step)}
            />
          </Box>
        );
      }}
    </Formik>
  );
};